// 전투 이벤트 → 이펙트 배선(소유: VFX). 렌더(src/render/index.ts)는 매 이벤트를 여기로 넘기고, 매 프레임 update를 부른다.
// 기본 공격 참격 궤적(Ev.Slash b<4)은 Kael 모델 칼끝 데이터가 필요해 렌더→SlashFx가 그린다. 스킬 판정(Ev.Slash b=10+id)과
// 각성 참격·모으기는 SlashFx.sink로 여기 들어온다. 데미지 숫자는 DamageNumbers가 받는다.
//
// 그림은 vfx-library v1(텍스처 13장, TSL 스프라이트)을 텍스처별 InstancedMesh로 묶어 쓰고(src/vfx/lib), 불똥·파편·피는 ParticleFx(드로우콜 1~3)로 그린다.
// 혼전(25~40마리)에서 한 동작이 여러 마리를 때리면 이벤트 수가 많으므로, 한 시뮬레이션 틱(= 한 판정 순간)에 띄우는 큰 스프라이트 수에 상한을 둔다.
// 전체 화면 섬광은 쓰지 않는다(각성만 FlashLimiter·PostFx 경유, COMBAT-FEEL 5장). 피·혈흔은 성인 모드에서만.
// 프레임 루프·이벤트 처리 안에서 새 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { MOB_STATS, SKILLS } from '../shared/config';
import { Ev, type GameEvent } from '../shared/events';
import type { GameView } from '../shared/types';
import type { FlashLimiter } from './flash';
import type { ParticleFx } from './particles';
import type { SlashFx, SlashSink } from './slashes';
import { DodgeTrail } from './dodgeTrail';
import { CombatCoreFx } from './combatCore';
import { DashCrack, StormArcs, StormRange } from './skillFx';
import { TreeFx } from './treeFx';
import { SPEC } from './lib/specs';
import { VfxSystem, newBurst, type Burst } from './lib/system';
import { TRAIL_SHAPES } from './action/timing';
import { ActionVfx } from './action';

const TEX_BASE = (import.meta.env.BASE_URL ?? './') + 'assets/vfx/';
/** Ev.Hit flags(events.ts 주석이 정본) */
const F_CRIT = 1, F_FIN = 2, F_AWAKEN = 8, F_MULTI = 16, F_BLOCKED = 32, F_JUGGLE = 128, F_EXECUTE = 256, F_DOWNCHASE = 512;
/** 한 판정 순간(시뮬레이션 틱)에 띄우는 스프라이트 상한(배율 1). 넘는 타격·처치는 불똥·파편만 낸다. */
const PER_TICK = { flare: 4, heavy: 3, multi: 2, metal: 2, kill: 3, splat: 3, dust: 6 } as const;
/** 불똥 색(HDR). 영웅 고유색(진홍) + 흰 코어(AV-SPEC) */
const CRIMSON = [2.4, 0.3, 0.42] as const;
const METAL = [1.2, 1.28, 1.4] as const;
/** 라이라 영웅색(청록 바람 #3fd0c0, HDR). 라이라일 때 진홍 대신 쓴다(c022) */
const TEAL = [0.55, 2.0, 1.85] as const;
/** 접촉 불똥(시안 C13: 칼날이 닿은 점에서 휘두른 방향으로 퍼지는 주황·흰 짧은 줄기) */
const SPARK = [2.6, 1.05, 0.32] as const;
/** 방패 막힘 금속 불똥(청백). 진홍을 쓰지 않아 명중과 구별된다 */
const STEEL = [1.25, 1.5, 2.1] as const;
/** 저스트 회피 반짝(차가운 흰 청) */
const GLINT = [1.4, 1.75, 2.3] as const;
const GOLD = [2.4, 1.6, 0.45] as const;
/** KO 흰 파편(결투장: 결정타 순간 흰 조각이 사방으로) */
const WHITE = [2.2, 2.1, 2.0] as const;
/** 처치 파편 색(몹 종류: 0 고블린, 1 방패 고블린, 2 멧돼지) */
const DEBRIS = [0x5a6a3a, 0x6a5a44, 0x6a4630] as const;
const HALF_PI = Math.PI / 2;
const TAU = Math.PI * 2;
/** 몹 몸 반경 근사(m): 접촉점 = 몹 중심에서 영웅 쪽으로 이만큼 */
const CONTACT_R = 0.45;
/** QA 효과 기록 상한 */
const LOG_CAP = 600;
/** 칼날 폭풍 회차 호(COMBAT-FEEL 9.2): 반폭 135°(약 270°), 반경 띠, 높이(무릎~허리), 그리기·표시 틱(판정 간격 9틱보다 짧게) */
const STORM_ARC = { half: 2.36, rIn: 1.95, rOut: 2.45, y: 0.72, draw: 3, end: 8 } as const;
/** 한 판정 순간에 '전량' 접촉 불똥을 내는 타격 수(배율 1). 넘으면 4개짜리 작은 불똥만(무리 속 스킬 적중이 흰 불똥 구름이 되지 않게) */
const FULL_SPARKS_PER_TICK = 4;
/** 돌진 경로를 따라 먼지·돌조각을 내는 간격(m) */
const DASH_EMIT_STEP = 1.6;
/** 균열 돌조각 색 */
const STONE = 0x7d6c58;

/** QA 효과 기록 한 줄(?qa 에서만 쌓는다) */
export interface VfxLogEntry { k: string; t: number; x: number; z: number; a: number; draw?: number; end?: number }

export interface CombatFxStats {
  scale: number; ready: boolean; sprites: number; batches: number; queued: number; dropped: number;
  particles: number; events: number; cpuMsPerFrame: number; particleCpuMsPerFrame: number; frames: number;
}

export class CombatFx implements SlashSink {
  /** 이 모듈이 따로 그리는 것(라이브러리 스프라이트 묶음)을 담는다. 렌더가 장면에 한 번 붙인다 */
  readonly group = new THREE.Group();
  private readonly vfx = new VfxSystem();
  /** 스킬 트리 연출: 진홍 초승달·추가 효과 6종·레벨 업 */
  private readonly tree: TreeFx;
  private scale = 1;
  private ready = false;
  private mature = false;
  // 판정 순간별 스프라이트 예산
  private budgetTick = -1;
  private bBlood = 0;
  private bFlare = 0; private bHeavy = 0; private bMulti = 0; private bMetal = 0; private bKill = 0; private bSplat = 0; private bDust = 0;
  private bSpark = 0;
  /**
   * true면 스킬 타임라인 큐를 VFX 모듈(src/vfx/modules, cueMap.ts)이 그린다. 모듈이 대신하는 기존 조각을 끈다(이중 그리기 방지):
   * 폭풍 마지막 고리, 낙화 착지 링 스프라이트 두 장, 초승달 바닥 긁힘, 런처 큰 초승달. 렌더가 모듈을 붙일 때 켠다(프리뷰·테스트는 꺼진 채 기존 연출).
   */
  skillsByModules = false;
  /** 스킬이 끊긴 순간(지금은 칼날 폭풍) 렌더에 알린다: 렌더가 VfxModules.cutSkill로 그 스킬의 지속 모듈을 지운다 */
  onSkillCut: ((skillId: number) => void) | null = null;
  /** 지금 영웅(c022). 라이라면 카엘 스킬 시전 연출(돌진 균열·폭풍 고리 등)을 내지 않고 타격·처치 강조색을 청록으로 */
  private lyra = false;
  private accent: readonly [number, number, number] = CRIMSON;
  setHeroId(hero: 'kael' | 'lyra' | undefined): void {
    this.lyra = hero === 'lyra';
    this.accent = this.lyra ? TEAL : CRIMSON;
    this.action.setHeroId(this.lyra, this.lyra ? 0x3fd0c0 : 0xe0313c);
  }
  /** 칼날 폭풍 회차(SkillCast 2에서 0으로) */
  private stormPulse = 0;
  /** 혈검 회전베기(8) 판정 회차(호 방향·각도 교대용) */
  private spinPulse = 0;
  /** 참격(Ev.Slash)이 나면 켜지고, 그 동작의 첫 타격이 임팩트 별을 내며 끈다 */
  private starArmed = false;
  /** 마지막 카메라 위치(섬광을 몸 앞쪽으로 당길 때) */
  private readonly cam = new THREE.Vector3(0, 20, 20);
  private px = 0; private py = 0; private pz = 0;
  private readonly B: Burst = newBurst();
  // QA 계측(누적)
  private cpuMs = 0;
  private frames = 0;
  private events = 0;
  private readonly counts = { sprites: 0, batches: 0, queued: 0 };
  private readonly debug: boolean;
  /** 섬광 약화(게임 설정): 타격 플레어·임팩트 별을 작게·회색 톤으로, 흰 불똥 비율을 낮춘다 */
  private reduce = false;
  /** 구르기 잔상 리본(실제 이동 경로) */
  private readonly dodgeTrail = new DodgeTrail();
  /** 칼날 폭풍 판정 반경 고리(시전~종료) */
  private readonly stormRange = new StormRange();
  /** 피의 돌진 경로 균열(실제 이동만큼) */
  private readonly dashCrack = new DashCrack();
  /** 칼날 폭풍 진행 중(취소 감지용) */
  private stormOn = false;
  /** 칼날 폭풍 시전 틱(시뮬레이션 틱). 끝까지 갔는지(자연 종료)와 끊겼는지를 가른다 */
  private stormCastTick = 0;
  /** 칼날 폭풍 회차 호(일반 투명 혼합, 앞 가장자리만 흰빛) */
  private readonly stormArcs = new StormArcs();
  /** 게임 영웅의 현재 스킬 id(렌더가 매 프레임 넘긴다, 0 = 스킬 아님)와 그 틱 */
  private heroSkill = 0;
  private simTick = 0;
  /** 마지막으로 받은 이벤트의 영웅 시계(틱): 초승달이 날아가는 동안의 타격 판별 */
  private evNow = 0;
  /** 마지막 update의 영웅 시계 */
  private lastNow = 0;
  private dashFacing = 0;
  private dashStart = 0;
  private dashEmitS = 0;
  private hx = 0; private hy = 0; private hz = 0;
  /** QA 효과 기록(?qa·개발 서버에서만 쌓는다). 종류: trail, spark, heavy, blocked, break, kill, dodgeTrail, justDodge, hurt,
   *  stormRange, stormArc(a=회차), stormEnd, skillCut(a=스킬 id), dashCrack(a=방향), dashEnd(x,z=끝점, a=실제 자란 길이 m) */
  readonly log: VfxLogEntry[] = [];
  /** 전투 코어 연출(공격 자석 링·무방비 표식·처형·공중 연타·폭주). QA: __VFX__.core.state() */
  readonly core: CombatCoreFx;
  /** 전투 액션 VFX(2026-10-27): 굵은 회전 베기 고리·타격 방사선·돌진 잔상(docs/ACTION-VFX-REF.md). QA: __VFX__.action.stats() */
  readonly action: ActionVfx;

  constructor(
    private readonly slashes: SlashFx,
    private readonly particles: ParticleFx,
    private readonly flash: FlashLimiter,
  ) {
    this.group.name = 'combat-fx';
    this.group.add(this.vfx.group);
    this.tree = new TreeFx(this.vfx, particles);
    this.group.add(this.tree.group);
    this.group.add(this.dodgeTrail.mesh);
    this.group.add(this.stormRange.group);
    this.group.add(this.stormArcs.group);
    this.group.add(this.dashCrack.mesh);
    this.core = new CombatCoreFx(particles, flash, (k, t, x, z, a) => this.note(k, t, x, z, a));
    this.group.add(this.core.group);
    this.action = new ActionVfx(slashes, particles);
    this.group.add(this.action.group);
    slashes.sink = this;
    this.vfx.onBurst = (b) => this.particles.streaks(b.x, b.y, b.z, b.dir, b.spread, b.count, b.speed, b.r, b.g, b.b, b.white, b.life, b.up);
    this.debug = typeof location !== 'undefined' && (new URLSearchParams(location.search).has('qa') || !!import.meta.env.DEV);
    // QA 훅: 배율 바꾸기·통계(?qa 또는 개발 서버에서만)
    if (this.debug) {
      (window as unknown as { __VFX__?: unknown }).__VFX__ = this;
      particles.profile = true;
    }
  }

  /** 텍스처 비동기 준비(실패해도 reject하지 않는다: 스프라이트만 안 보이고 불똥·파편은 그대로) */
  load(): Promise<void> {
    return this.vfx.load(TEX_BASE, TreeFx.EXTRA_TEXTURES).then(
      () => { this.ready = true; this.tree.bind(); },
      (err: unknown) => { console.warn('VFX 텍스처를 읽지 못해 불똥·파편만 그린다', err); },
    );
  }

  /** 품질 배율(0..1): 불똥·파편 수와 판정 순간별 스프라이트 상한에 곱한다. 0이면 새 효과를 내지 않는다. */
  setScale(s: number): void {
    this.scale = Math.max(0, Math.min(1, Number.isFinite(s) ? s : 1));
    this.core.setScale(this.scale);
    this.action.setScale(this.scale);
    this.tree.scale = this.scale;
    this.budgetTick = -1;
  }

  /** 섬광 약화 설정(매 프레임 불러도 된다: 값이 바뀔 때만 반영) */
  setReduceFlash(on: boolean): void {
    if (on === this.reduce) return;
    this.reduce = on;
    this.core.setReduceFlash(on);
    this.action.setReduceFlash(on);
    this.dodgeTrail.gain.value = on ? 0.6 : 1;
    this.dashCrack.gain.value = on ? 0.55 : 1;
    this.stormArcs.white.value = on ? 0.35 : 1;
    this.tree.setReduce(on);
  }

  get reduceFlash(): boolean { return this.reduce; }

  /** 게임 뷰·보간 계수·실시간 ms(매 프레임, update 전에): 공격 자석 링·무방비 표식이 몹을 따라간다. 연출은 읽기만 한다 */
  setView(view: GameView, alpha: number, realMs: number): void { this.core.setView(view, alpha, realMs); }

  /** 영웅 렌더 위치(매 프레임, update 전에): 구르기 잔상이 실제 경로를 따른다 */
  setHero(x: number, y: number, z: number): void { this.hx = x; this.hy = y; this.hz = z; }

  /**
   * 게임 영웅의 현재 스킬(매 프레임, update 전에; 읽기 전용). 칼날 폭풍이 끝나거나 끊기면(회피·피격·장면) 범위 고리와 남은 호를 정리하고,
   * 돌진 균열은 update에서 이 값으로 성장을 멈춘다. tick: 게임 틱(QA 기록용)
   */
  setHeroSkill(skillId: number, tick: number): void {
    this.simTick = tick;
    if (this.stormOn && skillId !== 2) {
      // 끊김 = 스킬 길이(54틱)를 다 채우기 전에 다른 상태로 넘어감. Codex P2(c013): 이전 판정(회차 6번 전)은 마지막 회차(45틱) 뒤
      // 46~53틱 회피·피격 끊김을 자연 종료로 보아 소용돌이(cutOnCancel)를 지우지 않았다. 1틱은 종료 틱 순서 차이 허용
      const cut = tick - this.stormCastTick < SKILLS[1].duration - 1;
      this.stormOn = false;
      this.stormRange.stop(this.lastNow, cut);
      if (cut) {
        this.stormArcs.cut();
        this.note('skillCut', tick, this.hx, this.hz, 2);
        this.onSkillCut?.(2);
      }
    }
    this.heroSkill = skillId;
  }

  /** QA: 효과 기록 비우기 */
  logReset(): void { this.log.length = 0; }

  /** QA 기록(디버그 빌드만) */
  private note(k: string, t: number, x: number, z: number, a = 0): void {
    if (!this.debug) return;
    if (this.log.length >= LOG_CAP) this.log.splice(0, this.log.length - LOG_CAP + 1);
    this.log.push({ k, t, x, z, a });
  }

  /** 렌더가 기본 공격 칼날 리본을 그릴 때 부른다(QA 기록만) */
  noteTrail(tick: number, step: number, x: number, z: number, draw: number, end: number): void {
    if (!this.debug) return;
    this.note('trail', tick, x, z, step);
    const e = this.log[this.log.length - 1];
    // 2026-10-27: 타별 표시 틱(TRAIL_SHAPES)이 정본. end 인자는 렌더 호환용 최댓값이다
    e.draw = draw; e.end = Math.min(end, (TRAIL_SHAPES[step] ?? TRAIL_SHAPES[0]).ticks);
  }

  /** now: 영웅 시계(틱), mature: 성인 모드, heroX/Z: 영웅 위치 */
  onEvent(e: GameEvent, now: number, mature: boolean, heroX: number, heroZ: number): void {
    if (this.scale <= 0) return;
    const t0 = this.debug ? performance.now() : 0;
    if (this.mature && !mature) this.vfx.clearSpec(SPEC.splat);
    this.mature = mature;
    this.evNow = now;
    this.budget(e.tick);
    this.events++;
    switch (e.type) {
      case Ev.Hit: this.hit(e); break;
      case Ev.Kill: this.kill(e); break;
      case Ev.MassKill: this.massKill(heroX, heroZ, now); break;
      case Ev.Dodge: this.dodge(e, now); break;
      case Ev.JustDodge: this.justDodge(e); break;
      case Ev.GuardBreak: this.guardBreak(e, heroX, heroZ); break;
      case Ev.Juggle: this.core.juggle(e); this.action.at(now).juggle(e.x, e.y, e.z, e.d); break;
      case Ev.Execute: this.core.execute(e); break;
      case Ev.Rampage: this.core.rampage(e); break;
      case Ev.SkillCast: this.skillCast(e, now); break;
      case Ev.SkillBurst: this.tree.burst(e, now); break;
      case Ev.LevelUp: this.tree.levelUp(e, now); break;
      case Ev.Ko: this.ko(e); break;
      case Ev.TelegraphFire:
        this.slashes.ring(e.x, e.z, 2.6, 0xff6a2a, now, 10);
        this.particles.dust(e.x, e.z, this.n(8));
        break;
      case Ev.BossIntro:
        this.slashes.ring(e.x, e.z, 8, 0xc9b38a, now, 24);
        this.particles.dust(e.x, e.z, this.n(24));
        this.dustRing(e.x, e.z, 4, 6, SPEC.dustBig, 1.6);
        break;
      case Ev.BossDefeated:
        this.slashes.ring(e.x, e.z, 10, 0xff2a3d, now, 30);
        this.particles.debris(e.x, e.z, 0, this.n(24), 0x5b4130);
        this.vfx.sprite(SPEC.ringGold, e.x, 0.07, e.z, 0, 5);
        this.vfx.later(0.1, SPEC.ring, e.x, 0.06, e.z, 0, 7);
        this.burst(e.x, 1.5, e.z, 0, TAU, 64, 13, GOLD, 0.5, 0.6, 5);
        this.dustRing(e.x, e.z, 3.5, 6, SPEC.dustBig, 1.8);
        break;
      default:
        break;
    }
    if (this.debug) this.cpuMs += performance.now() - t0;
  }

  // ── 이벤트별 연출 ─────────────────────────────────────

  /** 렌더가 참격(Ev.Slash) 순간에 부른다: 이 동작의 첫 타격이 임팩트 별을 낸다 */
  armStar(): void { this.starArmed = true; }

  private hit(e: GameEvent): void {
    const f = e.flags;
    // 다운 추격(HitFlag.DownChase): 누운 몸 높이에서 접촉 불똥이 난다(낮은 잿불 불똥·고리는 HitStateFx)
    const y = (f & F_DOWNCHASE) !== 0 && e.b !== 2 ? 0.35 : e.y > 0.2 ? e.y : 1;
    if (e.b === 2) {
      // 영웅 피격: 붉은 작은 섬광과 짧은 불똥(피 없음)
      this.burst(e.x, y, e.z, e.d, 2.6, 7, 6, CRIMSON, 0.3, 0.25, 2.5);
      if (this.bFlare > 0) { this.bFlare--; this.front(e.x, y, e.z, 0.4); this.vfx.sprite(SPEC.flareHurt, this.px, this.py, this.pz, 0, this.reduce ? 0.6 : 1, this.vfx.rand() * Math.PI); }
      this.note('hurt', e.tick, e.x, e.z);
      return;
    }
    // 접촉점: 몹 중심에서 영웅 쪽(e.d의 반대)으로 몸 반경만큼. 불똥·별은 칼날이 실제로 닿은 이 점에서 난다
    const sd = Math.sin(e.d), cd = Math.cos(e.d);
    const cx = e.x - sd * CONTACT_R, cz = e.z - cd * CONTACT_R;
    const fl = this.reduce ? 0.5 : 1;
    if (f & F_BLOCKED) {
      // 방패 정면: 청백 금속 불똥이 방패 앞에서 영웅 쪽으로 부채처럼 튄다 + 회색 고리(진홍·흰 별 없음, 시안 C13)
      this.burst(cx, y, cz, e.d + Math.PI, 2.6, 18, 9, STEEL, this.reduce ? 0.25 : 0.6, 0.3, 2.6);
      this.burst(cx, y, cz, e.d + Math.PI, 1.2, 4, 4, METAL, 0.3, 0.3, 1.2);
      if (this.bMetal > 0) {
        this.bMetal--;
        this.front(cx, y, cz, 0.35);
        this.vfx.sprite(SPEC.flareMetal, this.px, this.py, this.pz, 0, 1.2 * fl, this.vfx.rand() * Math.PI);
        this.vfx.sprite(SPEC.ringMetal, this.px, this.py, this.pz, 0, 1.6);
      }
      this.note('blocked', e.tick, cx, cz, f);
      return;
    }
    // 초승달이 날아가는 동안의 스킬 타격: 맞은 적 몸에 작은 진홍 섬광(판정 순간 예산 안에서)
    if (!this.lyra && (f & 4) !== 0 && this.bMulti > 0 && this.tree.waveLive(this.evNow)) {
      this.bMulti--;
      this.front(cx, y, cz, 0.5);
      this.vfx.sprite(SPEC.flareCrimson, this.px, this.py, this.pz, 0, this.reduce ? 0.55 : 1, this.vfx.rand() * Math.PI);
    }
    const heavy = (f & (F_CRIT | F_FIN)) !== 0 || e.b === 1;
    const exec = (f & F_EXECUTE) !== 0;
    if ((f & F_JUGGLE) !== 0) this.core.airHit(e.x, y, e.z);
    const multi = (f & F_MULTI) !== 0;
    // 결투장식 임팩트 별: 한 동작의 첫 타격에만 작은 흰 점 별을 접촉점 앞에 세운다(다단 중간타·각성 제외).
    // 2026-10-19: 2.3m 별·1.6m 플레어가 영웅·적 실루엣을 덮었다(기준 캡처) → 접촉점의 작은 핀포인트로 줄인다. 섬광 약화면 별을 내지 않는다
    if (this.starArmed && !multi && (f & F_AWAKEN) === 0) {
      this.starArmed = false;
      if (!this.reduce) {
        this.front(cx, y, cz, 0.6);
        this.vfx.sprite(SPEC.impactStar, this.px, this.py, this.pz, 0, e.b === 1 ? 0.7 : heavy ? 0.6 : 0.42, (this.vfx.rand() - 0.5) * 0.5);
      }
    }
    // 타격 방사선(2026-10-27): 접촉점 앞 흰 별 줄기(판정 순간당 상한은 ActionVfx). 각성 난무는 각성 연출이 맡는다
    if ((f & F_AWAKEN) === 0) {
      this.front(cx, y, cz, 0.55);
      this.action.at(this.evNow).hit(e.tick, this.px, this.py, this.pz, e.d, heavy || (f & 4) !== 0, multi);
    }
    // 휘두른 방향(e.d)으로 퍼지는 접촉 불똥: 일반은 주황·흰 짧은 부채, 강타는 진홍+흰 더 길고 많이
    const full = this.bSpark > 0;
    if (full) this.bSpark--;
    if (heavy) this.burst(cx, y, cz, e.d, 1.7, multi || !full ? 5 : 18, 11, this.accent, this.reduce ? 0.2 : 0.55, 0.3, 3);
    else this.burst(cx, y, cz, e.d, 1.5, multi || !full ? 4 : 13, 9, SPARK, this.reduce ? 0.2 : 0.65, 0.26, 2.4);
    if (multi) {
      if (this.bMulti > 0) { this.bMulti--; this.front(cx, y, cz, 0.5); this.vfx.sprite(SPEC.flareSmall, this.px, this.py, this.pz, 0, 0.6 * fl, this.vfx.rand() * Math.PI); }
    } else if (heavy && this.bHeavy > 0) {
      this.bHeavy--;
      // 강타(치명·마무리·스킬): 몸을 꿰뚫는 진홍 줄기 + 바닥 고리 + 중간 크기 플레어(일반보다 크지만 실루엣을 덮지 않게 1.6→1.0)
      this.front(cx, y, cz, 0.6);
      this.vfx.sprite(this.reduce ? SPEC.flareSmall : SPEC.flare, this.px, this.py, this.pz, 0, (e.b === 1 ? 1.25 : 1.0) * fl, this.vfx.rand() * Math.PI);
      // 처형은 교차 참격·충격 고리(CombatCoreFx)가 대신한다: 같은 자리에 진홍 줄기·고리를 겹치지 않는다
      // 라이라: 진홍 줄기 대신 바닥 고리만(화살 관통 표시는 LyraFx.pierceHit)
      if (!exec) {
        if (!this.lyra) this.vfx.sprite(SPEC.hitStreak, e.x, y, e.z, e.d, e.b === 1 ? 1.5 : 1.1, -HALF_PI);
        this.vfx.sprite(SPEC.ring, e.x, 0.05, e.z, 0, e.b === 1 ? 1.6 : 1.0);
      }
    } else if (this.bFlare > 0) {
      this.bFlare--;
      // 일반: 작은 따뜻한 플레어(0.6배 ≈ 0.7m)
      this.front(cx, y, cz, 0.5);
      this.vfx.sprite(SPEC.flareSmall, this.px, this.py, this.pz, 0, 0.6 * fl, this.vfx.rand() * Math.PI);
    }
    this.note(heavy ? 'heavy' : 'spark', e.tick, cx, cz, f);
    if (this.mature) {
      const drops = Math.min(this.bBlood, this.n(multi ? 2 : heavy ? 9 : 6));
      this.bBlood -= drops;
      this.particles.blood(e.x, y, e.z, e.d, drops);
      if (heavy && this.bSplat > 0) {
        this.bSplat--;
        this.vfx.sprite(SPEC.splat, e.x + Math.sin(e.d) * 0.5, 0.03, e.z + Math.cos(e.d) * 0.5, 0, 0.65, -e.d);
      }
    }
  }

  private kill(e: GameEvent): void {
    const cause = e.d | 0;
    const big = cause >= 1;
    const kind = MOB_STATS[e.a | 0] ? e.a | 0 : 0;
    // 몹은 e.c 방향으로 튕겨 날아간다(렌더가 그린다). 처치 지점에는 파편·먼지가 남는다.
    this.particles.debris(e.x, e.z, e.c, this.n((kind === 2 ? 9 : 6) * (big ? 1.5 : 1)), DEBRIS[kind] ?? DEBRIS[0]);
    // 바닥 먼지 입자는 판정 순간 먼지 예산 안에서만(칼날 폭풍·돌진이 여러 마리를 함께 쓰러뜨리면 먼지가 무리를 덮었다)
    if (this.bDust > 0) { this.bDust--; this.particles.dust(e.x, e.z, this.n(3)); }
    if (this.bKill > 0) {
      this.bKill--;
      const k = cause === 2 ? 2.1 : big ? 1.7 : 1.2;
      this.front(e.x, 0.8, e.z, 0.6);
      this.vfx.sprite(SPEC.flareSmall, this.px, this.py, this.pz, 0, k * (this.reduce ? 0.45 : 0.8), this.vfx.rand() * Math.PI);
      this.vfx.sprite(SPEC.ringKill, e.x, 0.05, e.z, 0, k * 0.9);
      // 2026-10-19 스킬 연출: 칼날 폭풍 마지막 회차에 여러 마리가 함께 쓰러지면 흰 먼지 구름이 무리를 덮었다 → 먼지를 0.8·0.65배로 낮게
      this.vfx.sprite(big ? SPEC.dustBig : SPEC.dust, e.x - 0.3, 0.4, e.z, 0, 0.8, this.vfx.rand() * TAU);
      this.vfx.sprite(SPEC.dust, e.x + 0.3, 0.3, e.z + 0.2, 0, 0.65, this.vfx.rand() * TAU);
      this.burst(e.x, 0.8, e.z, e.c, 2.2, big ? 20 : 12, big ? 10 : 7, this.accent, 0.5, 0.35, 4);
    } else {
      this.burst(e.x, 0.8, e.z, e.c, 2.4, 6, 6, this.accent, 0.4, 0.3, 3);
    }
    this.note('kill', e.tick, e.x, e.z, cause);
    if (this.mature) {
      // 큰 원형 웅덩이를 겹치지 않는다. 방울의 실제 착지점과 기존 splatter만 남긴다.
      if (this.bSplat > 0) { this.bSplat--; this.vfx.sprite(SPEC.splat, e.x, 0.03, e.z, 0, kind === 2 ? 0.9 : 0.75, -e.c); }
    }
  }

  private massKill(x: number, z: number, now: number): void {
    // 한 동작 20+ 처치: 큰 충격파 두 겹 + 사방 불똥 + 흙먼지 고리(전체 화면 섬광 없음)
    this.slashes.ring(x, z, 7, 0xffd36a, now, 14);
    this.vfx.sprite(SPEC.ring, x, 0.06, z, 0, 3.6);
    this.vfx.later(0.08, SPEC.ringGold, x, 0.07, z, 0, 5.2);
    this.burst(x, 0.9, z, 0, TAU, 56, 12, this.accent, 0.5, 0.45, 4);
    this.dustRing(x, z, 2.6, 6, SPEC.dustBig, 1.4);
  }

  /** 결투장식 KO(엘리트·방의 마지막 몹·보스): 큰 흰 별 + 흰 파편 + 바닥 고리, 정지가 풀리면 붉은 X 도장 */
  private ko(e: GameEvent): void {
    const boss = (e.a | 0) === 2;
    const y = boss ? 2.2 : 1.0, k = boss ? 1.8 : 1.3;
    this.front(e.x, y, e.z, 1.0);
    this.vfx.sprite(SPEC.impactStar, this.px, this.py, this.pz, 0, (this.reduce ? 0.6 : 1.2) * k, 0.2);
    this.vfx.later(0.1, SPEC.koStamp, this.px, this.py, this.pz, 0, k, 0.12);
    this.vfx.sprite(SPEC.ringGold, e.x, 0.07, e.z, 0, 2.6 * k);
    this.burst(e.x, y, e.z, 0, TAU, 44, 14, WHITE, this.reduce ? 0.3 : 1, 0.42, 4);
  }

  /**
   * 구르기(REL-021): 출발점 발 먼지 + 실제 이동 경로를 따르는 짧은 잔상 리본(DodgeTrail) + 도착 무렵 발 먼지.
   * 2026-10-19: 진행 방향 앞에 고정해 깔던 잔상 줄기는 몸이 그 안으로 굴러 들어가 관절·칼을 가렸다 → 몸 뒤 리본으로 바꾼다.
   */
  private dodge(e: GameEvent, now: number): void {
    const a = e.a, sx = Math.sin(a), cz = Math.cos(a);
    const bx = e.x - sx * 0.4, bz = e.z - cz * 0.4;
    this.vfx.sprite(SPEC.dust, bx, 0.25, bz, 0, 0.75, this.vfx.rand() * TAU);
    this.vfx.later(0.12, SPEC.dust, e.x + sx * 1.4, 0.22, e.z + cz * 1.4, 0, 0.6, this.vfx.rand() * TAU);
    this.vfx.later(0.3, SPEC.dust, e.x + sx * 3.4, 0.22, e.z + cz * 3.4, 0, 0.55, this.vfx.rand() * TAU);
    this.particles.dust(bx, bz, this.n(4));
    this.dodgeTrail.begin(now, this.hx, this.hz);
    this.note('dodgeTrail', e.tick, e.x, e.z, a);
  }

  /** 저스트 회피 성공: 발밑 차가운 흰청 반짝 고리 + 짧은 흰청 불똥(섬광 아님). 늦은 회피 실패는 영웅 피격(붉은 불똥·숫자)으로 구별된다 */
  private justDodge(e: GameEvent): void {
    this.vfx.sprite(SPEC.ringJust, e.x, 0.06, e.z, 0, 1);
    this.vfx.later(0.06, SPEC.ringJust, e.x, 0.07, e.z, 0, 1.5);
    this.front(e.x, 1.0, e.z, 0.5);
    this.vfx.sprite(SPEC.flareMetal, this.px, this.py, this.pz, 0, this.reduce ? 0.5 : 0.9, this.vfx.rand() * Math.PI);
    this.burst(e.x, 0.6, e.z, 0, TAU, 14, 5, GLINT, this.reduce ? 0.2 : 0.6, 0.3, 2);
    this.note('justDodge', e.tick, e.x, e.z);
  }

  /**
   * 가드 브레이크(REL-020): 막힘(청백 금속)과 구별되는 금빛 바닥 고리 + 방패 파편 + 사방 금빛 불똥.
   * 몹(a=0)은 맞은 방향으로 파편이 튀고, 보스(a=1)는 크게.
   */
  private guardBreak(e: GameEvent, heroX: number, heroZ: number): void {
    const boss = (e.a | 0) === 1;
    const dir = Math.atan2(e.x - heroX, e.z - heroZ);
    const k = boss ? 2.6 : 1.1;
    this.vfx.sprite(SPEC.ringBreak, e.x, 0.07, e.z, 0, k);
    this.vfx.later(0.07, SPEC.ringBreak, e.x, 0.08, e.z, 0, k * 1.5);
    this.front(e.x, boss ? 2.0 : 0.95, e.z, 0.6);
    this.vfx.sprite(SPEC.flareSmall, this.px, this.py, this.pz, 0, (boss ? 1.4 : 0.85) * (this.reduce ? 0.5 : 1), this.vfx.rand() * Math.PI);
    this.particles.debris(e.x, e.z, dir, this.n(boss ? 16 : 9), 0x8d939c);
    this.burst(e.x, boss ? 2.0 : 0.95, e.z, dir, boss ? TAU : 3.4, boss ? 36 : 20, 9, GOLD, this.reduce ? 0.2 : 0.5, 0.36, 3.5);
    this.core.guardBreak(e);
    this.note('break', e.tick, e.x, e.z, e.a);
  }

  private skillCast(e: GameEvent, now: number): void {
    // 라이라 스킬 시전 연출은 스킬 큐(LyraFx)가 맡는다: 카엘 번호 분기(돌진 균열·폭풍 범위 고리·초승달 불똥·낙화 룬)를 타지 않는다
    if (this.lyra) return;
    const id = e.a | 0, a = e.b;
    if (id === 1) {
      // 피의 돌진 시작: 뒤로 낮은 흙먼지 + 실제 경로를 따라 자라는 바닥 균열(COMBAT-FEEL 9.3). 고정 거리 연출은 쓰지 않는다
      this.vfx.sprite(SPEC.dust, e.x - Math.sin(a) * 0.5, 0.25, e.z - Math.cos(a) * 0.5, 0, 0.8, this.vfx.rand() * TAU);
      this.particles.dust(e.x, e.z, this.n(4));
      this.dashCrack.begin(now, e.x, this.hy, e.z, a, SKILLS[0].radius);
      this.dashFacing = a; this.dashStart = now; this.dashEmitS = DASH_EMIT_STEP;
      this.action.afterimage(now, this.hx, this.hz);
      this.note('dashCrack', e.tick, e.x, e.z, a);
    } else if (id === 2) {
      // 칼날 폭풍: 판정 반경 5m 얇은 바닥 고리(시전~종료). 지름 10m 회전 룬은 바닥·경고선을 덮어 쓰지 않는다(COMBAT-FEEL 9.2)
      this.stormPulse = 0;
      this.stormOn = true;
      this.stormCastTick = e.tick;
      this.stormRange.begin(now, e.x, this.hy, e.z, SKILLS[1].radius, SKILLS[1].duration);
      this.particles.dust(e.x, e.z, this.n(3));
      this.note('stormRange', e.tick, e.x, e.z, SKILLS[1].radius);
    } else if (id === 4) {
      // 진홍 초승달 선딜(0.15초 가로 휘두름): 발밑 먼지와 칼날 쪽 짧은 진홍 불똥만. 초승달은 판정 순간(Ev.Slash b=14)에 난다
      this.vfx.sprite(SPEC.dust, e.x - Math.sin(a) * 0.4, 0.22, e.z - Math.cos(a) * 0.4, 0, 0.6, this.vfx.rand() * TAU);
      this.burst(e.x + Math.sin(a) * 0.5, 0.9, e.z + Math.cos(a) * 0.5, a + HALF_PI, 1.6, 8, 6, CRIMSON, this.reduce ? 0.1 : 0.3, 0.2, 1.2);
    } else if (id === 6) {
      // C9 공중 난무 도약: 발밑 흙먼지 + 위로 튀는 진홍 불똥(고리·기둥은 큐 air_rise)
      this.particles.dust(e.x, e.z, this.n(5));
      this.burst(e.x, 0.5, e.z, 0, TAU, 8, 7, CRIMSON, this.reduce ? 0.1 : 0.25, 0.28, 4);
    } else if (id === 7) {
      // C9 대지 가르기 선딜: 발밑 먼지만(내려찍기·균열은 판정 순간과 큐)
      this.vfx.sprite(SPEC.dust, e.x + Math.sin(a) * 0.4, 0.22, e.z + Math.cos(a) * 0.4, 0, 0.6, this.vfx.rand() * TAU);
    } else if (id === 8) {
      // C9 혈검 회전베기 시전: 회차 번호 초기화 + 발밑 먼지·사방 진홍 불똥(슈퍼아머 시작, 오라는 큐 spin_aura)
      this.spinPulse = 0;
      this.particles.dust(e.x, e.z, this.n(5));
      this.burst(e.x, 0.7, e.z, 0, TAU, 10, 7, CRIMSON, this.reduce ? 0.1 : 0.25, 0.3, 2);
    } else if (id === 3) {
      // 붉은 낙화: 제자리 도약 → 착지점 룬, 도약 흙먼지
      this.vfx.sprite(SPEC.runeFall, e.x, 0.05, e.z, 0, 1);
      this.vfx.sprite(SPEC.dustBig, e.x, 0.35, e.z, 0, 1.1, 0);
      this.particles.dust(e.x, e.z, this.n(8));
    }
  }

  /** SlashSink: 스킬 판정 순간(Ev.Slash b=10+id). e.x,z 영웅 위치, e.a 방향, e.c 반경 */
  skillSlash(e: GameEvent, now: number): void {
    if (this.scale <= 0 || this.lyra) return;
    const id = e.b - 10, a = e.a, sx = Math.sin(a), cz = Math.cos(a);
    if (id === 4) {
      // 진홍 초승달: 길이 8m를 0.3초에 나는 초승달·바닥 자국·끝점 불꽃(treeFx)
      // 바닥 긁힘은 모듈 큐 crescent_wave의 균열이 그린다(skillsByModules)
      this.tree.crescent(e, now, !this.skillsByModules);
    } else if (id === 1) {
      // 피의 돌진(0.42초): 출발 순간 앞쪽으로 짧은 진홍 불똥만. 경로 균열·끝 충격은 실제 이동을 따라 update에서 그린다(COMBAT-FEEL 9.3).
      // 2026-10-19: 고정 7m 줄기·고정 거리 참격·7m 지점 충격은 벽에 막혀 멈춰도 벽 너머에 그려졌다(기준 캡처 dash_p27)
      this.burst(e.x + sx * 0.6, 0.8, e.z + cz * 0.6, a, 1.0, 8, 9, CRIMSON, this.reduce ? 0.2 : 0.45, 0.24, 1.5);
    } else if (id === 2) {
      // 칼날 폭풍(0.9초, 6회): 판정 회차마다 영웅 둘레 낮고 얇은 호 하나(60°씩 돌고 방향을 번갈아), 마지막 회차는 5m까지 퍼지는 얇은 고리와 작은 불똥.
      // 2026-10-19: 초승달 두 장(4.2m)·회차 불똥 12개·마지막 큰 섬광 2.0배·불똥 48개는 혼잡 웨이브에서 영웅·적·바닥을 덮었다(기준 캡처 storm_p12·p47)
      const k = this.stormPulse++;
      const f = a + k * (Math.PI / 3), dir = (k & 1) === 1 ? -1 : 1;
      const A = STORM_ARC;
      this.stormArcs.fire(now, e.x, this.hy + A.y, e.z, f, A.half, dir, A.rIn, A.rOut, A.draw, A.end);
      this.action.at(now).stormPulse(e.x, this.hy, e.z, a, k, e.c > 0 ? e.c : 5);
      this.note('stormArc', e.tick, e.x, e.z, k);
      if (k >= 5) {
        const r = e.c > 0 ? e.c : 5;
        // 마지막 고리는 모듈 큐 storm_finish가 그린다(skillsByModules)
        if (!this.skillsByModules) this.slashes.ring(e.x, e.z, r, 0xff2a3d, now, 14, 0.035);
        this.burst(e.x, 0.8, e.z, 0, TAU, 16, 8, CRIMSON, this.reduce ? 0.2 : 0.35, 0.3, 2.5);
        this.note('stormEnd', e.tick, e.x, e.z, r);
      }
    } else if (id === 3) {
      // 붉은 낙화 착지(반경 4.5m): 내리찍는 큰 초승달 + 충격파 두 겹 + 큰 섬광 + 사방 불똥·흙먼지
      const r = e.c > 0 ? e.c : 4.5;
      this.action.at(now).fallLand(e.x, this.hy, e.z, a, r);
      this.vfx.sprite(SPEC.slashBig, e.x + sx * 0.6, 1.2, e.z + cz * 0.6, a, 0.95, 0, false);
      // 착지 충격 고리 두 겹은 모듈 큐 fall_shockwave(링 둘·방사 균열·흙연기)가 그린다(skillsByModules)
      if (!this.skillsByModules) {
        this.vfx.sprite(SPEC.ring, e.x, 0.06, e.z, 0, r * 0.55);
        this.vfx.later(0.05, SPEC.ringKill, e.x, 0.07, e.z, 0, r * 1.05);
      }
      this.front(e.x, 1.0, e.z, 0.6);
      // c013(모듈이 그릴 때): 흰 플레어 4.2m·큰 흙먼지 5장(한 장 3~4.5m, 반경 1.8m)이 모듈 흙연기와 겹쳐 영웅을 덮는 회색 덩어리가 됐다.
      // 흙연기 플립북은 큐에서 뺐고(중복 제거), 먼지는 충격파 앞(반경 3m)의 작은 먼지 6장으로, 플레어는 2.6m로 줄여 영웅 실루엣을 남긴다
      this.vfx.sprite(SPEC.flare, this.px, this.py, this.pz, 0, this.skillsByModules ? 1.6 : 2.6, 0.4);
      this.burst(e.x, 0.6, e.z, 0, TAU, 64, 13, CRIMSON, 0.5, 0.45, 5);
      if (this.skillsByModules) this.dustRing(e.x, e.z, 3.0, 6, SPEC.dust, 0.75);
      else this.dustRing(e.x, e.z, 1.8, 5, SPEC.dustBig, 1.4);
    } else if (id === 6) {
      // C9 공중 난무: 5타는 영웅 둘레 높은 호 + 위로 튀는 불똥(예산 8개), 착지 마무리(반경 3.8)는 큰 섬광·먼지·불똥 40개. 호·고리는 큐 air_slash·air_land
      const r = e.c > 0 ? e.c : 3.4;
      if (r >= 3.7) {
        this.front(e.x, 1.0, e.z, 0.6);
        this.vfx.sprite(SPEC.flare, this.px, this.py, this.pz, 0, this.reduce ? 1.0 : 1.8, 0.4);
        this.burst(e.x, 0.6, e.z, 0, TAU, 40, 12, CRIMSON, 0.45, 0.4, 4.5);
        this.dustRing(e.x, e.z, 2.6, 5, SPEC.dust, 0.75);
        this.note('airLand', e.tick, e.x, e.z, r);
      } else {
        this.burst(e.x + sx * 1.2, this.hy + 1.0, e.z + cz * 1.2, a, 2.4, 8, 9, CRIMSON, this.reduce ? 0.1 : 0.3, 0.26, 2.5);
        this.note('airSlash', e.tick, e.x, e.z, r);
      }
    } else if (id === 7) {
      // C9 대지 가르기 내려찍기(반경 2.6): 칼 꽂힌 자리 전방 먼지 고리·돌조각·불똥 30개. 균열이 번지는 모양은 큐 split_impact·split_crack
      this.front(e.x + sx * 0.9, 0.9, e.z + cz * 0.9, 0.6);
      this.vfx.sprite(SPEC.flare, this.px, this.py, this.pz, 0, this.reduce ? 0.9 : 1.6, 0.4);
      this.particles.debris(e.x + sx * 1.0, e.z + cz * 1.0, a, this.n(10), 0x6a5a52);
      this.burst(e.x + sx * 1.0, 0.5, e.z + cz * 1.0, a, 2.6, 30, 11, CRIMSON, 0.4, 0.4, 4.5);
      this.dustRing(e.x + sx * 1.0, e.z + cz * 1.0, 2.2, 5, SPEC.dust, 0.7);
      this.note('splitImpact', e.tick, e.x, e.z, e.c);
    } else if (id === 8) {
      // C9 혈검 회전베기(9회전): 폭풍과 같은 낮은 호를 반경 4.2m에 맞춰 번갈아 돌리고(회차당 불똥 6개), 마지막 회차(9번째)는 큰 고리 불똥 28개
      const k = this.spinPulse++;
      const f = a + k * (Math.PI / 3), dir = (k & 1) === 1 ? -1 : 1;
      const A = STORM_ARC, rr = (e.c > 0 ? e.c : 4.2) / 5;
      this.stormArcs.fire(now, e.x, this.hy + A.y, e.z, f, A.half, dir, A.rIn * rr * 1.4, A.rOut * rr * 1.4, A.draw, A.end);
      this.burst(e.x, 0.8, e.z, f, TAU, 6, 8, CRIMSON, this.reduce ? 0.1 : 0.25, 0.26, 1.5);
      if (k >= 8) this.burst(e.x, 0.8, e.z, 0, TAU, 28, 9, CRIMSON, 0.35, 0.32, 2.5);
      this.note('spinArc', e.tick, e.x, e.z, k);
    } else if (id === 5) {
      // 천공 올려베기(런처, COMBAT-FEEL 2.11): 전방에서 위로 올려베는 큰 초승달 + 위로 튀는 불똥. 최소 연결(전용 VFX는 D1d 모듈 이후)
      // 큰 초승달은 VFX 모듈(Ev.SkillCue launcher_arc_up → 아크·트레일·링·균열)이 그린다. 모듈이 없을 때(프리뷰·테스트)만 이 스프라이트를 쓴다
      if (!this.skillsByModules) this.vfx.sprite(SPEC.slashBig, e.x + sx * 0.9, 1.4, e.z + cz * 0.9, a, 0.85, 0, true);
      this.action.at(now).launcher(e.x, this.hy, e.z, a);
      this.burst(e.x + sx * 1.2, 0.8, e.z + cz * 1.2, a, 1.6, 14, 7, CRIMSON, 0.35, 0.3, 5);
    }
  }

  /** SlashSink: 기본 공격 참격 순간(마무리 4타 굵은 고리 등, ActionVfx) */
  basicSlash(step: number, x: number, y: number, z: number, yaw: number, radius: number, now: number): void {
    this.action.at(now).basicSlash(step, x, y, z, yaw, radius, now);
  }

  /** SlashSink: 각성 난무 1타(AwakenFx → SlashFx.awakenLine) */
  awakenLine(x: number, z: number, angle: number, radius: number): void {
    if (this.scale <= 0) return;
    void radius;
    this.vfx.sprite(SPEC.slashAwaken, x, 1.2, z, angle, 1.15, this.vfx.rand() - 0.5, this.vfx.rand() > 0.5);
    this.vfx.sprite(SPEC.ring, x, 0.06, z, 0, 2.0);
    this.burst(x, 1.0, z, angle, 3.2, 40, 12, CRIMSON, 0.5, 0.35, 3);
  }

  /** SlashSink: 각성 모으기(발밑 진홍 룬 서클 2.4초) */
  awakenGather(x: number, z: number): void {
    if (this.scale <= 0) return;
    this.vfx.sprite(SPEC.runeAwaken, x, 0.05, z, 0, 1);
  }

  // ── 도우미 ─────────────────────────────────────────────

  /** 판정 순간이 바뀌면 스프라이트 예산을 채운다 */
  private budget(tick: number): void {
    if (tick === this.budgetTick) return;
    this.budgetTick = tick;
    this.bBlood = this.budgetCount(24);
    this.bFlare = this.budgetCount(PER_TICK.flare); this.bHeavy = this.budgetCount(PER_TICK.heavy); this.bMulti = this.budgetCount(PER_TICK.multi); this.bMetal = this.budgetCount(PER_TICK.metal);
    this.bKill = this.budgetCount(PER_TICK.kill); this.bSplat = this.budgetCount(PER_TICK.splat); this.bDust = this.budgetCount(PER_TICK.dust);
    this.bSpark = this.budgetCount(FULL_SPARKS_PER_TICK);
  }

  private budgetCount(v: number): number { return this.scale >= 1 ? v : Math.max(1, Math.round(v * this.scale)); }

  /** 입자 수에 품질 배율을 곱한다 */
  private n(v: number): number { return Math.round(v * this.scale); }

  private burst(x: number, y: number, z: number, dir: number, spread: number, count: number, speed: number, c: readonly [number, number, number], white: number, life: number, up: number): void {
    this.particles.streaks(x, y, z, dir, spread, this.n(count), speed, c[0], c[1], c[2], white, life, up);
  }

  /** (x,y,z)에서 카메라 쪽으로 d만큼 당긴 점(섬광이 몸에 가려지지 않게) → this.px/py/pz */
  private front(x: number, y: number, z: number, d: number): void {
    const dx = this.cam.x - x, dy = this.cam.y - y, dz = this.cam.z - z;
    const l = Math.hypot(dx, dy, dz) || 1;
    this.px = x + (dx / l) * d; this.py = y + (dy / l) * d; this.pz = z + (dz / l) * d;
  }

  /** 반경 r 둘레에 먼지 count장(판정 순간 예산 안에서) */
  private dustRing(x: number, z: number, r: number, count: number, spec: typeof SPEC.dust | typeof SPEC.dustBig, k: number): void {
    for (let i = 0; i < count && this.bDust > 0; i++) {
      this.bDust--;
      const a = (i / count) * TAU + 0.4;
      this.vfx.sprite(spec, x + Math.sin(a) * r, 0.4, z + Math.cos(a) * r, 0, k, i);
    }
  }

  /** 매 렌더 프레임. now: 영웅 시계(틱), dt: 영웅 시간 경과(틱, 히트스톱·일시정지 0), camera: 빌보드용, mature: 성인 모드(끄면 피 자국을 즉시 지운다) */
  update(now: number, dt: number, camera: THREE.Camera, mature = this.mature): void {
    const t0 = this.debug ? performance.now() : 0;
    if (this.mature && !mature) this.vfx.clearSpec(SPEC.splat);
    this.mature = mature;
    this.cam.copy(camera.position);
    this.vfx.update(dt / 60, camera as THREE.PerspectiveCamera);
    this.dodgeTrail.update(now, this.hx, this.hy, this.hz);
    this.lastNow = now;
    this.tree.update(now, this.hx, this.hy, this.hz, this.cam.x, this.cam.z);
    this.stormRange.update(now, this.hx, this.hy, this.hz);
    this.stormArcs.update(now);
    this.core.update(dt / 60, camera);
    this.action.update(now, dt / 60, camera, this.hx, this.hy, this.hz);
    if (this.dashCrack.isShown) this.dashUpdate(now);
    if (this.debug) { this.cpuMs += performance.now() - t0; this.frames++; }
  }

  /** 돌진 균열 성장·경로 먼지·끝 충격(실제로 멈춘 자리) */
  private dashUpdate(now: number): void {
    const c = this.dashCrack;
    const ended = c.update(now, this.hx, this.hy, this.hz, this.heroSkill === 1);
    // 경로를 따라 낮은 먼지·돌조각(지나간 자리에만)
    while (c.travelled >= this.dashEmitS && this.scale > 0) {
      this.vfx.sprite(SPEC.dust, c.endX, 0.18, c.endZ, 0, 0.5, this.vfx.rand() * TAU);
      this.particles.debris(c.endX, c.endZ, this.dashFacing, this.n(2), STONE);
      this.dashEmitS += DASH_EMIT_STEP;
    }
    if (!ended) return;
    const ex = c.endX, ez = c.endZ;
    this.vfx.sprite(SPEC.ringKill, ex, 0.05, ez, 0, 1.0);
    this.vfx.sprite(SPEC.dust, ex, 0.25, ez, 0, 0.7, this.vfx.rand() * TAU);
    this.burst(ex, 0.6, ez, this.dashFacing, 1.6, 10, 7, CRIMSON, this.reduce ? 0.2 : 0.4, 0.28, 2);
    this.note('dashEnd', this.simTick, ex, ez, c.travelled);
    // 돌진 길이(25틱)보다 일찍 끝났으면 끊김(회피·피격)
    if (now - this.dashStart < SKILLS[0].duration - 2) this.note('skillCut', this.simTick, ex, ez, 1);
  }

  /** QA 읽기: 스킬 연출 상태 */
  skillFx(): { stormRing: boolean; stormArcs: number; crackLen: number; crackEnd: [number, number] | null; crackActive: boolean } {
    const c = this.dashCrack;
    return { stormRing: this.stormRange.visible, stormArcs: this.stormArcs.active, crackLen: c.travelled, crackEnd: c.isShown ? [c.endX, c.endZ] : null, crackActive: c.isGrowing };
  }

  /** QA: 현재 상태와 누적 CPU 시간(onEvent+update)을 프레임 평균으로 */
  stats(reset = false): CombatFxStats {
    this.vfx.counts(this.counts);
    const s: CombatFxStats = {
      scale: this.scale, ready: this.ready, sprites: this.counts.sprites, batches: this.counts.batches, queued: this.counts.queued,
      dropped: this.vfx.dropped, particles: this.particles.activeCount, events: this.events,
      cpuMsPerFrame: this.frames > 0 ? this.cpuMs / this.frames : 0,
      particleCpuMsPerFrame: this.frames > 0 ? this.particles.cpuMs / this.frames : 0, frames: this.frames,
    };
    if (reset) { this.cpuMs = 0; this.frames = 0; this.events = 0; this.vfx.dropped = 0; this.particles.cpuMs = 0; }
    return s;
  }

  prewarm(on: boolean): void {
    this.vfx.prewarm(on);
    this.stormRange.prewarm(on);
    this.stormArcs.prewarm(on);
    this.dashCrack.prewarm(on);
    this.core.prewarm(on);
    this.action.prewarm(on);
    this.tree.prewarm(on);
  }

  clear(): void {
    this.vfx.reset();
    this.dodgeTrail.clear();
    this.stormRange.clear();
    this.stormArcs.clear();
    this.dashCrack.clear();
    this.core.clear();
    this.action.clear();
    this.tree.clear();
    this.stormOn = false;
    this.budgetTick = -1;
    this.stormPulse = 0;
    this.spinPulse = 0;
    this.starArmed = false;
  }

  dispose(): void {
    void this.flash;
    if (this.slashes.sink === this) this.slashes.sink = null;
    this.vfx.dispose();
    this.dodgeTrail.dispose();
    this.stormRange.dispose();
    this.stormArcs.dispose();
    this.dashCrack.dispose();
    this.core.dispose();
    this.action.dispose();
    this.tree.dispose();
    if (this.debug) {
      const w = window as unknown as { __VFX__?: unknown };
      if (w.__VFX__ === this) delete w.__VFX__;
    }
  }
}
